import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Gift, Landmark } from 'lucide-react';
import { formatCurrency } from '@/lib/i18n';
import { api } from '@/lib/api';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import type { Branch, PaginationMeta } from '@/lib/shared-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { idempotencyKey, moneyStep } from './lib';
import WalletAccountDetail from './WalletAccountDetail';
import {
  WalletAccountKind,
  WalletAccountStatus,
  apiError,
  type IssueGiftCardBody,
  type IssueStoreCreditBody,
  type IssuedGiftCard,
  type WalletAccount,
  type WalletPosting,
} from './walletApi';

const KINDS = Object.values(WalletAccountKind);
const STATUSES = Object.values(WalletAccountStatus);

/** A customer match, as `GET /customers?search=` answers it. */
interface CustomerMatch {
  id: string;
  name: string | null;
  phone: string | null;
}

/**
 * Gift cards and store credit — ADR-0012. `wallet.view` gates the route (see
 * `Routes.tsx`); every mutating control below is gated again on its own key,
 * because the backend spent eight separate permissions here rather than one
 * `wallet.manage` and the screen should not flatten that back down.
 *
 * There is no cross-account transaction search on the backend — only
 * `GET /wallet/:id/transactions`, per account. So "redemption history and
 * reconciliation" lives on the detail panel's ledger (`WalletAccountDetail`),
 * not as a separate screen here: that is what the contract actually exposes.
 */
export default function Wallet() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const canIssue = can('wallet.issue');

  const [rows, setRows] = useState<WalletAccount[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [issueCardOpen, setIssueCardOpen] = useState(false);
  const [issueCreditOpen, setIssueCreditOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ data: WalletAccount[]; meta: PaginationMeta }>('/wallet', {
        params: {
          page,
          limit: 25,
          kind: kind || undefined,
          status: status || undefined,
          search: search || undefined,
        },
      })
      .then(({ data }) => {
        setRows(data.data ?? []);
        setMeta(data.meta ?? null);
        setSelectedId((current) =>
          current && (data.data ?? []).some((row) => row.id === current)
            ? current
            : (data.data ?? [])[0]?.id ?? null,
        );
      })
      .catch((e) => setError(apiError(e, t('wallet.loadFailed'))))
      .finally(() => setLoading(false));
  }, [page, kind, status, search, t]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  // Search re-pages to 1 rather than showing page 3 of a suddenly-shorter result.
  useEffect(() => setPage(1), [kind, status, search]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">{t('nav.wallet')}</h1>
        <p className="text-sm text-ink-muted">{t('wallet.subtitle')}</p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <Field label={t('wallet.filterKind')} controlId="wallet-filter-kind">
          <Select
            id="wallet-filter-kind"
            className="w-44"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
          >
            <option value="">{t('wallet.allKinds')}</option>
            {KINDS.map((value) => (
              <option key={value} value={value}>
                {t(`wallet.kind.${value}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('wallet.filterStatus')} controlId="wallet-filter-status">
          <Select
            id="wallet-filter-status"
            className="w-44"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">{t('wallet.allStatuses')}</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(`wallet.status.${value}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={t('wallet.search')}
          hint={t('wallet.searchHint')}
          controlId="wallet-search"
        >
          <Input
            id="wallet-search"
            className="w-56"
            placeholder={t('wallet.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Field>
        {canIssue && (
          <div className="ms-auto flex gap-2">
            <Button variant="outline" onClick={() => setIssueCreditOpen(true)}>
              {t('wallet.issueCredit')}
            </Button>
            <Button onClick={() => setIssueCardOpen(true)}>{t('wallet.issueCard')}</Button>
          </div>
        )}
      </div>

      <div className="grid gap-4 xl:grid-cols-[22rem_1fr]">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle>{t('wallet.accounts')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              <LoadingState label={t('common.loading')} />
            ) : error ? (
              // Money screen: a failed read must never look like "no cards" —
              // this is its own state, with its own retry, never the empty one.
              <ErrorState message={error} onRetry={load} />
            ) : rows.length === 0 ? (
              <EmptyState
                title={
                  kind || status || search ? t('wallet.emptyFiltered') : t('wallet.empty')
                }
                hint={
                  kind || status || search ? t('wallet.emptyFilteredHint') : t('wallet.emptyHint')
                }
              />
            ) : (
              <ul className="scrollbar-thin max-h-[36rem] divide-y overflow-y-auto rounded-lg border">
                {rows.map((row) => (
                  <AccountRow
                    key={row.id}
                    row={row}
                    selected={row.id === selectedId}
                    onSelect={() => setSelectedId(row.id)}
                  />
                ))}
              </ul>
            )}

            {meta && meta.pages > 1 && (
              <div className="flex items-center justify-between text-xs text-ink-muted">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  {t('common.previous')}
                </Button>
                <span>{t('wallet.pageOf', { page: meta.page, pages: meta.pages })}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={page >= meta.pages}
                  onClick={() => setPage((p) => Math.min(meta.pages, p + 1))}
                >
                  {t('common.next')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <div>
          {selectedId ? (
            <WalletAccountDetail accountId={selectedId} onChanged={load} />
          ) : (
            <Card>
              <CardContent className="p-5">
                <EmptyState title={t('wallet.nothingSelected')} />
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {issueCardOpen && (
        <IssueGiftCardDialog
          onClose={() => setIssueCardOpen(false)}
          onIssued={(id) => {
            setSelectedId(id);
            load();
          }}
        />
      )}
      {issueCreditOpen && (
        <IssueStoreCreditDialog
          onClose={() => setIssueCreditOpen(false)}
          onIssued={(id) => {
            setSelectedId(id);
            load();
          }}
        />
      )}
    </div>
  );

  function AccountRow({
    row,
    selected,
    onSelect,
  }: {
    row: WalletAccount;
    selected: boolean;
    onSelect: () => void;
  }) {
    const isCard = row.kind === WalletAccountKind.GIFT_CARD;
    return (
      <li>
        <button
          onClick={onSelect}
          className={`w-full p-3 text-start text-sm hover:bg-hover ${
            selected ? 'bg-accent-soft' : ''
          }`}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-2 font-medium">
              {isCard ? (
                <Gift className="h-4 w-4 shrink-0 text-accent" aria-hidden />
              ) : (
                <Landmark className="h-4 w-4 shrink-0 text-accent" aria-hidden />
              )}
              <span className="truncate">
                {isCard
                  ? row.codeLast4
                    ? t('wallet.maskedCode', { last4: row.codeLast4 })
                    : t('wallet.giftCard')
                  : (row.customer?.name ?? t('wallet.storeCreditFor', { id: row.customerId ?? '—' }))}
              </span>
            </span>
            <Badge tone={toneForStatus(row.status)}>{t(`wallet.status.${row.status}`)}</Badge>
          </div>
          <div className="mt-0.5 flex items-center justify-between gap-2 text-xs text-ink-muted">
            <span>{isCard && row.customer?.name ? row.customer.name : t(`wallet.kind.${row.kind}`)}</span>
            <span className="tabular-nums font-medium text-ink">
              {formatCurrency(row.balance, row.currency, i18n.language)}
            </span>
          </div>
        </button>
      </li>
    );
  }
}

/**
 * Any customer this venue has on file, as a small search-then-pick control.
 * `IssueGiftCardDto.customerId` and `IssueStoreCreditDto.customerId` both need
 * an EXISTING customer id — the service 404s otherwise — so this is a lookup,
 * not a free-text field.
 */
export function CustomerCombobox({
  value,
  onChange,
  required,
}: {
  value: { id: string; label: string } | null;
  onChange: (value: { id: string; label: string } | null) => void;
  required?: boolean;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<CustomerMatch[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!query.trim()) {
      setMatches([]);
      return;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      api
        .get<{ data: CustomerMatch[] }>('/customers', { params: { search: query, limit: 6 } })
        .then(({ data }) => setMatches(data.data ?? []))
        .catch(() => setMatches([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-lg border p-2.5 text-sm">
        <span className="truncate font-medium">{value.label}</span>
        <Button size="sm" variant="ghost" onClick={() => onChange(null)}>
          {t('wallet.changeCustomer')}
        </Button>
      </div>
    );
  }

  return (
    <Field label={t('wallet.customer')} required={required} hint={t('wallet.customerHint')}>
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('wallet.customerSearchPlaceholder')}
      />
      {query.trim() && (
        <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border text-sm">
          {searching ? (
            <p className="p-2 text-ink-muted">{t('common.loading')}</p>
          ) : matches.length === 0 ? (
            <p className="p-2 text-ink-muted">{t('wallet.customerNoMatch')}</p>
          ) : (
            matches.map((match) => (
              <button
                key={match.id}
                type="button"
                className="block w-full p-2 text-start hover:bg-hover"
                onClick={() => {
                  onChange({
                    id: match.id,
                    label: [match.name ?? t('wallet.unnamedCustomer'), match.phone]
                      .filter(Boolean)
                      .join(' · '),
                  });
                  setQuery('');
                  setMatches([]);
                }}
              >
                {match.name ?? t('wallet.unnamedCustomer')}
                {match.phone && <span className="text-ink-muted"> · {match.phone}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </Field>
  );
}

/** A branch picker. `GET /branches` carries no permission key of its own. */
function useBranches() {
  const [branches, setBranches] = useState<Branch[]>([]);
  useEffect(() => {
    api
      .get<{ data: Branch[] }>('/branches', { params: { limit: 100 } })
      .then(({ data }) => setBranches(data.data ?? []))
      .catch(() => setBranches([]));
  }, []);
  return branches;
}

/**
 * §2 — issue a gift card, with the policy fields the backend actually exposes:
 * `reloadable`, `transferable` and `expiresAt` on `IssueGiftCardDto`.
 *
 * The plaintext code is in the response of THIS call and never again (ADR-0012
 * Decision 4) — the dialog does not close on success, it switches to a reveal
 * step with a copy affordance, because there is no second chance to show it.
 */
function IssueGiftCardDialog({
  onClose,
  onIssued,
}: {
  onClose: () => void;
  onIssued: (accountId: string) => void;
}) {
  const { t } = useTranslation();
  const branches = useBranches();
  const [branchId, setBranchId] = useState('');
  const { decimals, code: currencyCode } = useCurrency(branchId || null);
  const step = moneyStep(currencyCode);

  const [amount, setAmount] = useState('');
  const [customer, setCustomer] = useState<{ id: string; label: string } | null>(null);
  const [activate, setActivate] = useState(false);
  const [reloadable, setReloadable] = useState(true);
  const [transferable, setTransferable] = useState(true);
  const [expiresAt, setExpiresAt] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<IssuedGiftCard | null>(null);
  const [key] = useState(() => idempotencyKey('wallet-issue-card'));

  const valid = branchId && Number(amount) > 0;

  const submit = async () => {
    setBusy(true);
    try {
      const body: IssueGiftCardBody = {
        branchId,
        amount: Number(amount).toFixed(decimals),
        customerId: customer?.id,
        activate,
        reloadable,
        transferable,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
        note: note || undefined,
        idempotencyKey: key,
      };
      const { data } = await api.post<IssuedGiftCard>('/wallet/gift-cards', body);
      setIssued(data);
    } catch (e) {
      toast.error(apiError(e, t('wallet.issueCardFailed')));
    } finally {
      setBusy(false);
    }
  };

  if (issued) {
    return (
      <Dialog
        open
        onClose={() => {
          onIssued(issued.account.id);
          onClose();
        }}
        title={t('wallet.cardIssued')}
        description={t('wallet.cardIssuedHint')}
        footer={
          <Button
            onClick={() => {
              onIssued(issued.account.id);
              onClose();
            }}
          >
            {t('wallet.codeSaved')}
          </Button>
        }
      >
        <div className="space-y-3">
          <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
            {t('wallet.codeShownOnce')}
          </p>
          <div className="flex items-center justify-between gap-2 rounded-lg border p-4">
            <code className="text-lg font-semibold tracking-wide">{issued.code}</code>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (issued.code) void navigator.clipboard?.writeText(issued.code);
                toast.success(t('wallet.codeCopied'));
              }}
            >
              {t('common.copy')}
            </Button>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('wallet.issueCard')}
      description={t('wallet.issueCardHint')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={busy || !valid}>
            {t('wallet.issueCard')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('wallet.branch')} required controlId="issue-card-branch">
          <Select
            id="issue-card-branch"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
          >
            <option value="">{t('wallet.pickBranch')}</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name} ({b.currency})
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label={t('wallet.amount')}
          required
          hint={t('currency.amountIn', { code: currencyCode })}
          controlId="issue-card-amount"
        >
          <Input
            id="issue-card-amount"
            type="number"
            inputMode="decimal"
            min="0"
            step={step}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>

        <CustomerCombobox value={customer} onChange={setCustomer} />

        <div className="grid gap-3 sm:grid-cols-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={activate}
              onChange={(e) => setActivate(e.currentTarget.checked)}
            />
            {t('wallet.activateNow')}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={reloadable}
              onChange={(e) => setReloadable(e.currentTarget.checked)}
            />
            {t('wallet.reloadable')}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={transferable}
              onChange={(e) => setTransferable(e.currentTarget.checked)}
            />
            {t('wallet.transferable')}
          </label>
        </div>
        <p className="text-xs text-ink-muted">{t('wallet.activateHint')}</p>

        <Field label={t('wallet.expiresAt')} hint={t('wallet.expiresAtHint')}>
          <Input
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </Field>

        <Field label={t('wallet.note')} hint={t('wallet.noteHint')}>
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

/**
 * §4 — store credit, minted for a named customer out of a refund or an act of
 * compensation. `reason` is mandatory on the DTO, same discipline as the manual
 * adjustment: an unattributed credit is indistinguishable from a mistake.
 */
function IssueStoreCreditDialog({
  onClose,
  onIssued,
}: {
  onClose: () => void;
  onIssued: (accountId: string) => void;
}) {
  const { t } = useTranslation();
  const branches = useBranches();
  const [branchId, setBranchId] = useState('');
  const { decimals, code: currencyCode } = useCurrency(branchId || null);
  const step = moneyStep(currencyCode);

  const [customer, setCustomer] = useState<{ id: string; label: string } | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [refType, setRefType] = useState('');
  const [refId, setRefId] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [key] = useState(() => idempotencyKey('wallet-issue-credit'));

  const valid = branchId && customer && Number(amount) > 0 && reason.trim().length > 0;

  const submit = async () => {
    if (!customer) return;
    setBusy(true);
    try {
      const body: IssueStoreCreditBody = {
        customerId: customer.id,
        branchId,
        amount: Number(amount).toFixed(decimals),
        reason: reason.trim(),
        refType: refType || undefined,
        refId: refId || undefined,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
        idempotencyKey: key,
      };
      const { data } = await api.post<WalletPosting>('/wallet/store-credit', body);
      toast.success(t('wallet.creditIssued'));
      onIssued(data.account.id);
      onClose();
    } catch (e) {
      toast.error(apiError(e, t('wallet.issueCreditFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('wallet.issueCredit')}
      description={t('wallet.issueCreditHint')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={busy || !valid}>
            {t('wallet.issueCredit')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <CustomerCombobox value={customer} onChange={setCustomer} required />

        <Field label={t('wallet.branch')} required controlId="issue-credit-branch">
          <Select
            id="issue-credit-branch"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
          >
            <option value="">{t('wallet.pickBranch')}</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name} ({b.currency})
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label={t('wallet.amount')}
          required
          hint={t('currency.amountIn', { code: currencyCode })}
          controlId="issue-credit-amount"
        >
          <Input
            id="issue-credit-amount"
            type="number"
            inputMode="decimal"
            min="0"
            step={step}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>

        <Field
          label={t('wallet.reason')}
          required
          hint={t('wallet.creditReasonHint')}
          controlId="issue-credit-reason"
        >
          <Input
            id="issue-credit-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('wallet.refType')} hint={t('wallet.refTypeHint')}>
            <Input value={refType} onChange={(e) => setRefType(e.target.value)} />
          </Field>
          <Field label={t('wallet.refId')}>
            <Input value={refId} onChange={(e) => setRefId(e.target.value)} />
          </Field>
        </div>

        <Field label={t('wallet.expiresAt')} hint={t('wallet.creditExpiresHint')}>
          <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
